import {classNames} from "services/string";
import React, {useEffect, useState} from "react";
import {DateTime, Interval} from "luxon";
import Spinner from "components/Spinner";
import {Event} from 'types/Event';
import {ClockIcon, EyeOffIcon} from "@heroicons/react/outline";
import {Menu} from "@headlessui/react";
import {CalendarView} from "./EventSchedule";
import EventContextMenu from "components/menus/EventContextMenu";
import EventPreview from "components/EventPreview";
import {formattedTime, isSameTimezone} from "../../../services/date";
import settings from "../../../services/ngage/settings";
import {useOperator} from "../../../context/OperatorContext";

function isToday(date: DateTime): boolean {
  return DateTime.now().startOf('day').equals(date.startOf('day'))
}

type CalendarItem = { date: Interval, events: Array<Event>}

function createCalendarItems(dates: Array<Interval>, eventList: Array<Event>) {
  if (!eventList) return []

  // Create an array of objects with the date and events associated with that date 
  let days: Array<CalendarItem> = dates.map(date => {
    const events = eventList.filter(event => event.starts_at.toISODate() === date.start.toISODate())

    return { date, events }
  })

  return days;
}

type MonthViewProps = {
  dates: Array<Interval>
  events: Array<Event> | undefined
  selectedDate: DateTime
  selectDate: (date: DateTime) => void
  selectView: (view: CalendarView) => void
  isLoading: boolean
  onDeleteEvent: (event: Event) => void
}

function MonthView({dates, events, selectedDate, selectDate, selectView, isLoading, onDeleteEvent}: MonthViewProps) {
  const {settings} = useOperator();
  const [calendarItems, setCalendarItems] = useState<Array<CalendarItem>>([])
  const [selectedItem, setSelectedItem] = useState<CalendarItem | null>(null)

  useEffect(() => {
    if (!events || !dates) return

    setCalendarItems(createCalendarItems(dates, events))
  }, [dates, events]);

  function deleteEvent(event: Event) {
    // TODO: Update the selected item when an event is deleted instead of deselecting the item. This is an issue
    // on mobile where the deleted event isn't removed from the UI.
    setSelectedItem(null)

    onDeleteEvent(event)
  }

  return (
    <>
      <div className="shadow ring-1 ring-black ring-opacity-5 lg:flex lg:flex-auto lg:flex-col">

        {/* Day Headers */}
        <div className="grid grid-cols-7 gap-px border-b border-gray-300 bg-gray-200 text-center text-xs font-semibold leading-6 text-gray-700 lg:flex-none">
          <div className="bg-white py-2">
            M<span className="sr-only sm:not-sr-only">on</span>
          </div>
          <div className="bg-white py-2">
            T<span className="sr-only sm:not-sr-only">ue</span>
          </div>
          <div className="bg-white py-2">
            W<span className="sr-only sm:not-sr-only">ed</span>
          </div>
          <div className="bg-white py-2">
            T<span className="sr-only sm:not-sr-only">hu</span>
          </div>
          <div className="bg-white py-2">
            F<span className="sr-only sm:not-sr-only">ri</span>
          </div>
          <div className="bg-white py-2">
            S<span className="sr-only sm:not-sr-only">at</span>
          </div>
          <div className="bg-white py-2">
            S<span className="sr-only sm:not-sr-only">un</span>
          </div>
        </div>

        {/* Calendar body */}
        <div className="flex bg-gray-100 text-xs leading-6 text-gray-700 lg:flex-auto relative">
          {!!calendarItems.length &&
            <>
              {/* Desktop calendar body */}
              <div className="hidden w-full lg:grid lg:grid-cols-7 lg:grid-rows-[repeat(5,_minmax(130px,_1fr))] lg:auto-rows-[minmax(130px,_1fr)] lg:gap-px">
                {calendarItems.map(({date, events}) => {
                  const IS_TODAY = isToday(date.start)
                  const IS_CURRENT_MONTH = date.start.month === selectedDate.month

                  return (
                    <a
                      href='#'
                      key={date.start.toFormat('yyyy-MM-dd')}
                      onClick={() => {
                        selectView('day')
                        selectDate(date.start)
                      }}
                      className={classNames(
                        IS_CURRENT_MONTH ? 'bg-white' : 'bg-gray-50 text-gray-500',
                        'relative px-3 py-2 hover:bg-gray-50 transition-colors duration-150 focus-visible:outline-brand'
                      )}
                    >
                      <time
                        dateTime={date.start.toISO()}
                        className={
                          IS_TODAY
                            ? 'flex h-6 w-6 items-center justify-center rounded-full bg-brand font-semibold text-white'
                            : undefined
                        }
                      >
                        {date.start.day}
                      </time>

                      {!!events.length && (
                        <ol className="mt-2">
                          {events.map((event) => (
                            <li key={event.id}>
                              <Menu as="div">
                                <Menu.Button className="group flex w-full focus-visible:outline-brand" onClick={(e) => e.stopPropagation()}>
                                  <div className='flex items-center gap-2'>
                                    {!event.is_published && <EyeOffIcon className="h-3.5 w-3.5 text-gray-800"/>}
                                    <p className="text-left flex-1 truncate font-medium text-gray-900 group-hover:text-brand">
                                      {event.name}
                                    </p>
                                    <time
                                      dateTime={event.starts_at.toISO()}
                                      className="ml-3 hidden flex-none text-gray-500 group-hover:text-brand xl:block"
                                    >
                                      {event.is_all_day ? 'All day' : event.starts_at.setZone(event.timezone).setLocale(settings?.localisation.locale ?? 'local').toLocaleString(DateTime.TIME_SIMPLE)}
                                    </time>
                                  </div>
                                </Menu.Button>

                                <EventPreview event={event} onDeleteEvent={() => deleteEvent(event)} />
                              </Menu>
                            </li>
                          ))}
                        </ol>
                      )}
                    </a>
                  )})}
              </div>
            </>
          }

          {/* Mobile calendar body */}
          <div className="isolate grid w-full grid-cols-7 grid-rows-[repeat(5,_minmax(60px,_1fr))] auto-rows-[minmax(60px,_1fr)] gap-px lg:hidden">
            {calendarItems.map(({date, events}) => {
              const IS_SELECTED = (selectedItem?.date.start.day === date.start.day) && (selectedItem?.date.start.month === date.start.month)
              const IS_TODAY = isToday(date.start)
              const IS_CURRENT_MONTH = date.start.month === selectedDate.month

              return (
                <button
                  key={date.start.toFormat('yyyy-MM-dd')}
                  type="button"
                  className={classNames(
                    IS_CURRENT_MONTH ? 'bg-white' : 'bg-gray-50',
                    (IS_SELECTED || IS_TODAY) && 'font-semibold',
                    IS_SELECTED && 'text-white',
                    !IS_SELECTED && IS_TODAY && 'text-brand',
                    !IS_SELECTED && IS_CURRENT_MONTH && !IS_TODAY && 'text-gray-900',
                    !IS_SELECTED && !IS_CURRENT_MONTH && !IS_TODAY && 'text-gray-500',
                    'flex flex-col px-3 py-2 hover:bg-gray-100 focus:z-10'
                  )}
                  onClick={() => {
                    selectDate(date.start)
                    setSelectedItem({date, events})
                  }}
                >
                  <time
                    dateTime={date.start.toISO()}
                    className={classNames(
                      IS_SELECTED && IS_TODAY && 'bg-brand',
                      IS_SELECTED && !IS_TODAY && 'bg-gray-900',
                      'flex h-6 w-6 items-center justify-center rounded-full ml-auto'
                    )}
                  >
                    {date.start.day}
                  </time>
                  <span className="sr-only">{events.length} events</span>

                  {!!events.length && (
                    <span className="-mx-0.5 mt-auto flex flex-wrap-reverse">
                        {events.map((event) => (
                          <span key={event.id} className="mx-0.5 mb-1 h-1.5 w-1.5 rounded-full bg-gray-400" />
                        ))}
                      </span>
                  )}
                </button>
            )})}
          </div>

          {/* Loading spinner */}
          {isLoading &&
            <div className='absolute bg-white inset-0 flex items-center justify-center'>
              <Spinner color={Spinner.colors.BRAND} size={Spinner.sizes.LARGE}/>
            </div>
          }
        </div>
      </div>

      {/* Mobile event list for selected day */}
      {!!selectedItem?.events.length && (
        <div className="pt-10 lg:hidden">
          <h3>Events - {selectedItem.date.start.toFormat('DDD')}</h3>
          <ol className="divide-y divide-gray-100 overflow-hidden rounded-lg bg-white text-sm shadow ring-1 ring-black ring-opacity-5 mt-4">
            {selectedItem.events.map((event) => {
              const sameTimezone = isSameTimezone(event?.timezone);

              return (
              <li key={event.id} className="group flex p-4 pr-2 focus-within:bg-gray-50 hover:bg-gray-50">
                <div className="flex-auto">
                  <div className="flex items-center gap-2">
                    {!event.is_published && <EyeOffIcon className="h-3 w-3 text-gray-800"/>}
                    <p className="font-semibold text-gray-900">{event.name}</p>
                  </div>
                  <p className="text-gray-400 mt-1">{event.type.name}</p>

                  {(sameTimezone || event.is_all_day) ?
                    <time dateTime={event.starts_at.toISO()} className="mt-3 flex items-center text-gray-700">
                      <ClockIcon className="mr-2 h-5 w-5 text-gray-400" aria-hidden="true" />
                      {event.is_all_day ? 'All day' : formattedTime(event.starts_at, false, undefined, settings?.localisation.locale)}
                    </time> :
                    <>
                      <time dateTime={event.starts_at.setZone(event.timezone).toISO()} className="mt-3 flex items-center text-gray-700">
                        <ClockIcon className="mr-2 h-5 w-5 text-gray-400" aria-hidden="true" />
                        {event.is_all_day ? 'All day' : formattedTime(event.starts_at, true, event.timezone, settings?.localisation.locale)}
                      </time>

                      <time dateTime={event.starts_at.toISO()} className="mt-3 flex items-center text-gray-700">
                        <ClockIcon className="mr-2 h-5 w-5 text-gray-400" aria-hidden="true" />
                        {event.is_all_day ? 'All day' : formattedTime(event.starts_at, true, undefined, settings?.localisation.locale)}
                      </time>
                    </>
                  }

                </div>

                <EventContextMenu event={event} onDeleteEvent={() => deleteEvent(event)} />
              </li>
            )})}
          </ol>
        </div>
      )}
    </>
  )
}

export default MonthView